import React, { useState, useEffect } from 'react';
import { HttpService } from '../../game/services/HttpService';
import './Friends.component.css';

interface Friend {
  id: string;
  friend: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  creationTime: string;
}

interface FriendRequest {
  id: string;
  requester: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  receiver: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  status: string;
  creationTime: string;
}

interface FriendsProps {
  onClose: () => void;
  onJoinFriendGame?: (roomId: string) => void;
  onOpenChat?: (friendId: string, friendName: string) => void;
}

const Friends: React.FC<FriendsProps> = ({ onClose, onJoinFriendGame, onOpenChat }) => {
  const [friends, setFriends] = useState<Friend[]>([]);
  const [receivedRequests, setReceivedRequests] = useState<FriendRequest[]>([]);
  const [sentRequests, setSentRequests] = useState<FriendRequest[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'friends' | 'received' | 'sent' | 'search'>('friends');

  const httpService = new HttpService();
  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');

  useEffect(() => {
    const currentToken = localStorage.getItem('token');
    if (currentToken) {
      httpService.setToken(currentToken);
      loadFriends();
      loadReceivedRequests();
      loadSentRequests();
    }
  }, []);

  const loadFriends = async () => {
    try {
      const response = await httpService.get<any>('friends');
      if (response.status === 'success' && response.data) {
        setFriends(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error: any) {
      console.error('Error loading friends:', error);
    }
  };

  const loadReceivedRequests = async () => {
    try {
      const response = await httpService.get<any>('friends/requests/received');
      if (response.status === 'success' && response.data) {
        setReceivedRequests(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error: any) {
      console.error('Error loading received requests:', error);
    }
  };

  const loadSentRequests = async () => {
    try {
      const response = await httpService.get<any>('friends/requests/sent');
      if (response.status === 'success' && response.data) {
        setSentRequests(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error: any) {
      console.error('Error loading sent requests:', error);
    }
  };

  const searchUsers = async () => {
    const trimmedQuery = searchQuery.trim();
    
    if (!trimmedQuery) {
      setError('Please enter a search query');
      return;
    }

    if (trimmedQuery.length < 5) {
      setError('Search query must be at least 5 characters');
      return;
    }

    // Ensure token is set before making the request
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      setError('You must be logged in to search for users');
      return;
    }
    httpService.setToken(currentToken);

    setLoading(true);
    setError('');
    try {
      const response = await httpService.get<any>(`user/search/${encodeURIComponent(trimmedQuery)}`);
      if (response.status === 'success' && response.data) {
        setSearchResults(Array.isArray(response.data) ? response.data : []);
        setActiveTab('search');
      } else {
        setError(response.message || 'No users found');
        setSearchResults([]);
      }
    } catch (error: any) {
      setError(error.message || 'No users found');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const sendFriendRequest = async (receiverId: string) => {
    try {
      const response = await httpService.post<any>('friends/requests/send', {
        receiverId: receiverId,
      });
      if (response.status === 'success') {
        setError('');
        // Update the search results to reflect the new request status
        setSearchResults(prevResults => 
          prevResults.map(user => 
            user.id === receiverId ? { ...user, requestSent: true } : user
          )
        );
        loadSentRequests();
      } else {
        setError(response.message || 'Failed to send friend request');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to send friend request');
    }
  };

  const acceptFriendRequest = async (requesterId: string) => {
    try {
      const response = await httpService.post<any>('friends/requests/accept', {
        requesterId: requesterId,
      });
      if (response.status === 'success') {
        loadFriends();
        loadReceivedRequests();
        setError('');
      } else {
        setError(response.message || 'Failed to accept friend request');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to accept friend request');
    }
  };

  const rejectFriendRequest = async (requesterId: string) => {
    try {
      const response = await httpService.post<any>('friends/requests/reject', {
        requesterId: requesterId,
      });
      if (response.status === 'success') {
        loadReceivedRequests();
        setError('');
      } else {
        setError(response.message || 'Failed to reject friend request');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to reject friend request');
    }
  };

  const cancelFriendRequest = async (receiverId: string) => {
    try {
      const response = await httpService.post<any>('friends/requests/cancel', {
        receiverId: receiverId,
      });
      if (response.status === 'success') {
        loadSentRequests();
        setError('');
      } else {
        setError(response.message || 'Failed to cancel friend request');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to cancel friend request');
    }
  };

  const removeFriend = async (friendId: string) => {
    if (!window.confirm('Are you sure you want to remove this friend?')) {
      return;
    }
    try {
      const response = await httpService.post<any>('friends/remove', {
        friendId: friendId,
      });
      if (response.status === 'success') {
        loadFriends();
        setError('');
      } else {
        setError(response.message || 'Failed to remove friend');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to remove friend');
    }
  };

  const getFriendName = (friend: Friend) => {
    return `${friend.friend.firstName || ''} ${friend.friend.lastName || ''}`.trim() || friend.friend.email;
  };

  const getUserName = (user: any) => {
    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
  };

  return (
    <div className="friends-overlay">
      <div className="friends-modal">
        <div className="friends-header">
          <h2>Friends</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {error && (
          <div className="friends-error">{error}</div>
        )}

        <div className="friends-tabs">
          <button
            className={activeTab === 'friends' ? 'active' : ''}
            onClick={() => setActiveTab('friends')}
          >
            Friends ({friends.length})
          </button>
          <button
            className={activeTab === 'received' ? 'active' : ''}
            onClick={() => setActiveTab('received')}
          >
            Received ({receivedRequests.length})
          </button>
          <button
            className={activeTab === 'sent' ? 'active' : ''}
            onClick={() => setActiveTab('sent')}
          >
            Sent ({sentRequests.length})
          </button>
          <button
            className={activeTab === 'search' ? 'active' : ''}
            onClick={() => setActiveTab('search')}
          >
            Search
          </button>
        </div>

        <div className="friends-content">
          {activeTab === 'friends' && (
            <div className="friends-list">
              {friends.length === 0 ? (
                <p className="empty-message">No friends yet. Search for users to add them!</p>
              ) : (
                friends.map((friend) => (
                  <div key={friend.id} className="friend-item">
                    <div className="friend-info">
                      <div className="friend-name">{getFriendName(friend)}</div>
                      <div className="friend-email">{friend.friend.email}</div>
                    </div>
                    <div className="friend-actions">
                      {onOpenChat && (
                        <button
                          type="button"
                          className="message-friend-button"
                          onClick={() => onOpenChat(friend.friend.id, getFriendName(friend))}
                          title="Open chat"
                        >
                          Message
                        </button>
                      )}
                      <button
                        className="remove-friend-button"
                        onClick={() => removeFriend(friend.friend.id)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'received' && (
            <div className="friends-list">
              {receivedRequests.length === 0 ? (
                <p className="empty-message">No pending friend requests</p>
              ) : (
                receivedRequests.map((request) => (
                  <div key={request.id} className="friend-item">
                    <div className="friend-info">
                      <div className="friend-name">
                        {getUserName(request.requester)}
                      </div>
                      <div className="friend-email">{request.requester.email}</div>
                    </div>
                    <div className="friend-actions">
                      <button
                        className="accept-button"
                        onClick={() => acceptFriendRequest(request.requester.id)}
                      >
                        Accept
                      </button>
                      <button
                        className="reject-button"
                        onClick={() => rejectFriendRequest(request.requester.id)}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'sent' && (
            <div className="friends-list">
              {sentRequests.length === 0 ? (
                <p className="empty-message">No pending sent requests</p>
              ) : (
                sentRequests.map((request) => (
                  <div key={request.id} className="friend-item">
                    <div className="friend-info">
                      <div className="friend-name">
                        {getUserName(request.receiver)}
                      </div>
                      <div className="friend-email">{request.receiver.email}</div>
                    </div>
                    <button
                      className="cancel-button"
                      onClick={() => cancelFriendRequest(request.receiver.id)}
                    >
                      Cancel
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'search' && (
            <div className="search-friends">
              <div className="search-input-group">
                <input
                  type="text"
                  placeholder="Search by name or email (min 5 characters)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchUsers()}
                  maxLength={100}
                />
                <button 
                  onClick={searchUsers} 
                  disabled={loading || searchQuery.trim().length < 5}
                >
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </div>

              {error && searchQuery.trim().length >= 5 && (
                <div className="search-error">{error}</div>
              )}

              {searchQuery.trim().length > 0 && searchQuery.trim().length < 5 && (
                <div className="search-warning">
                  Please enter at least 5 characters to search
                </div>
              )}

              {searchResults.length > 0 && (
                <div className="search-results">
                  <div className="results-header">
                    Found {searchResults.length} {searchResults.length === 1 ? 'user' : 'users'}
                  </div>
                  <div className="friends-list">
                    {searchResults.map((user) => (
                      <div key={user.id} className="friend-item">
                        <div className="friend-info">
                          <div className="friend-name">{getUserName(user)}</div>
                          <div className="friend-email">{user.email}</div>
                        </div>
                        {user.id === userId ? (
                          <span className="self-indicator">This is you</span>
                        ) : friends.some(f => f.friend.id === user.id) ? (
                          <span className="already-friend">Already friends</span>
                        ) : sentRequests.some(r => r.receiver.id === user.id) ? (
                          <span className="request-sent">Request sent</span>
                        ) : (
                          <button
                            className="send-request-button"
                            onClick={() => sendFriendRequest(user.id)}
                          >
                            Send Request
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!loading && searchQuery.trim().length >= 5 && searchResults.length === 0 && !error && (
                <div className="empty-message">No users found</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Friends;

